import { yupResolver } from "@hookform/resolvers/yup";
import { Checkbox, Col, Form, Input, Row, Select, Space } from "antd";
import dayjs from "dayjs";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import entityType from "../../api/entityType";
import useUserPreferences from "../../hooks/useUserPreferences";
import { transformProductionInput } from "../../store/production/productionActions";
import {
  exportReport,
  fetchReport,
  resetReportState,
} from "../../store/report/reportActions";
import { listPolicyProducts } from "../../store/retention/retentionActions";
import PermissionWrapper from "../navigation/PermissionWrapper";
import ReportPanel from "../shared/ReportPanel";
import AgencyMultiSelectEx from "../shared/form/AgencyMultiSelectEx";
import BatchRangeSelectEx from "../shared/form/BatchRangeSelectEx";
import { DrillDownSortField } from "../shared/form/DrillDownSortField";
import DrillDownSortFieldSelectEx from "../shared/form/DrillDownSortFieldSelectEx";
import ExtraReportFilterContainer from "../shared/form/ExtraReportFiltersContainer";
import ProducerSelectEx from "../shared/form/ProducerSelectEx";
import SearchMultiSelectEx from "../shared/form/SearchMultiSelectEx";
import { SortGroupField } from "../shared/form/SortGroupField";
import SortGroupFieldSelectEx from "../shared/form/SortGroupFieldSelectEx";
import {
  dateRangeConfigs,
  fixedDateRanges,
} from "../shared/form/fixedDateRanges";
import {
  fleetGarageCommaListMessage,
  rateClassCommaListMessage,
} from "../shared/form/formValidatonRules";
import { getInitialAgency } from "../shared/form/getInitialAgency";
import AdvancedRangePicker from "../shared/form/reactHookForm/AdvancedRangePicker";
import CustomRadioGroup from "../shared/form/reactHookForm/CustomRadioGroup";
import { dateFormat, toEndOfDay, toStartOfDay } from "../shared/formatting";
import {
  ViewProductivityDataFullAccess,
  ViewProductivityDataLimitedAccess,
} from "../shared/securityRoleTasks";
import { agenciesListEqual } from "../shared/sorting";
import { productionReportSchema } from "./productionSchemas";
import {
  comparisonDrillDownFieldOptions,
  drillDownSortFieldOptions,
  productionReportTypeOptions,
  reportDateTypes,
  reportTypes,
  salesAndCondensedDrillDownFieldOptions,
  sortGroupDrillDownFields,
  sortGroupFieldOptions,
} from "./reportParameters";
import { useLocation } from "react-router";
import { applyProductionReportUrlDefaults } from "./productionReportUrlHelpers";

// Constants
const partialInitialValues = {
  AgencyKeys: [],
  ReportType: reportTypes.SUMMARY,
  FixedDateRange: fixedDateRanges.MTD,
  TransactionDate: ["", ""],
  IncludeNonFleetNonGarage: true,
  IncludeFleetGarage: true,
  IncludeCancellations: false,
  SortGroupFields: [],
  DrillDownSortFields: [],
  FleetGaragePolicyNumbers: "",
  Customer: "",
  ProducerCode1Keys: [],
  ProducerCode2Keys: [],
  BatchNumberFrom: null,
  BatchNumberTo: null,
  ProductKeys: [],
  RateClass: "",
  IncludeLeased: true,
  IncludeNotLeased: true,
  DateType: reportDateTypes.TRANSACTION_DATE,
};

const defaultLayout = {
  labelCol: {
    xs: { span: 5 },
    sm: { span: 5 },
    md: { span: 5 },
    lg: { span: 8 },
    xl: { span: 6 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 19 },
    sm: { span: 19 },
    md: { span: 19 },
    lg: { span: 16 },
    xl: { span: 18 },
    xxl: { span: 19 },
  },
};

const specialLayout = {
  labelCol: {
    xs: { span: 5 },
    sm: { span: 5 },
    md: { span: 5 },
    lg: { span: 8 },
    xl: { span: 5 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 19 },
    sm: { span: 19 },
    md: { span: 19 },
    lg: { span: 16 },
    xl: { span: 19 },
    xxl: { span: 19 },
  },
};

const ProductionReport = () => {
  // Form
  const {
    handleSubmit,
    reset,
    control,
    getValues,
    setValue,
    trigger,
    watch,
    formState: { isValid, isDirty, errors, defaultValues, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: partialInitialValues,
    resolver: yupResolver(productionReportSchema),
  });

  //
  const includeFleetGarage = watch("IncludeFleetGarage");

  //
  const dispatch = useDispatch();
  const location = useLocation();

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [initialDefaultValues, setInitialDefaultValues] =
    useState(partialInitialValues);
  const [initialDefaultAgency, setInitialDefaultAgency] = useState(null);

  //
  const actorTasks = useSelector((state) => state.actor.details.data.Tasks);
  const agencies = useSelector((state) => state.agency.listCompact);
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const { preferences } = useUserPreferences(["UseYesterdayInToDateShortcuts"]);
  const toDateEndingIsYesterday = preferences?.UseYesterdayInToDateShortcuts;
  const taskKey = actorTasks.includes(ViewProductivityDataFullAccess)
    ? ViewProductivityDataFullAccess
    : ViewProductivityDataLimitedAccess;
  const policyProducts = useSelector((state) => state.retention.policyProducts);

  useEffect(() => {
    if (isEmpty(policyProducts)) {
      dispatch(listPolicyProducts());
    }
  }, [policyProducts]);

  useEffect(() => {
    if (
      agencies != null &&
      !isEmpty(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      var initialAgency = getInitialAgency(homeAgency, agencies);
      setInitialDefaultAgency(initialAgency);

      const fullInitialValues = {
        ...partialInitialValues,
        AgencyKeys: [initialAgency],
        TransactionDate: [
          toStartOfDay(dayjs().startOf("month")),
          toEndOfDay(dayjs().endOf("day")),
        ],
      };

      // Transform default values with URL params
      const merged = applyProductionReportUrlDefaults(fullInitialValues, {
        agenciesList: agencies,
        homeAgencyKey: homeAgency,
        location,
      });

      setInitialDefaultValues(cloneDeep(merged));
      reset(merged);
      trigger();

      // Reset default options for drilldown
      getExtraDisableDefaultOptions([initialAgency], getValues().ReportType);
    }
  }, [agencies, homeAgency]);

  // Review what should happen when fields are already pre-selected and we change something
  const getExtraDisableDefaultOptions = (agencyKeys, reportType) => {
    const fieldName =
      reportType === reportTypes.DETAIL
        ? "SortGroupFields"
        : "DrillDownSortFields";
    let fieldValue =
      reportType === reportTypes.DETAIL
        ? new SortGroupField()
        : new DrillDownSortField();
    if (agencyKeys.length === 1 && agencyKeys[0] !== 0) {
      if (reportType === reportTypes.DETAIL) {
        fieldValue.Name = sortGroupDrillDownFields.BATCH_NUMBER;
        setValue(fieldName, [fieldValue]);
      } else {
        setValue(fieldName, []);
      }
    } else if (
      (agencyKeys.length === 1 && agencyKeys[0] === 0) ||
      agencyKeys.length > 1
    ) {
      fieldValue.Name = sortGroupDrillDownFields.AGENCY_NAME;
      setValue(fieldName, [fieldValue]);
    }
  };

  const onSubmitHandler = (values) => {
    dispatch(
      fetchReport(
        transformProductionInput(values, taskKey),
        entityType.PRODUCTION,
        `Print${values.ReportType}`,
        () => {
          reset(values);
        }
      )
    );
  };

  const handlers = {
    onSubmit: () => handleSubmit(onSubmitHandler)(),
    onExport: () =>
      dispatch(
        exportReport(
          transformProductionInput(getValues(), taskKey),
          `Production${getValues().ReportType}Report`,
          entityType.PRODUCTION
        )
      ),
    onRestore: () => {
      const reportType = getValues().ReportType;
      reset({ ...initialDefaultValues, ReportType: reportType });
      dispatch(resetReportState());
    },
  };

  const buttons = [
    {
      action: "preview",
      disabled: !isValid,
    },
    {
      action: "export",
      disabled: !isValid,
      onClick: handlers.onExport,
    },
    { action: "restore", onClick: handlers.onRestore },
  ];

  return (
    <PermissionWrapper entityName="ProductionReports">
      <ReportPanel
        isDirty={isDirty}
        isSubmitted={isSubmitted}
        onSubmit={handlers.onSubmit}
        buttons={buttons}
        taskId={ViewProductivityDataLimitedAccess}
      >
        <Row>
          <Col xs={24} sm={24} md={24} lg={10} xl={10} xxl={10}>
            <Form.Item
              {...defaultLayout}
              label="Report Type"
              name="ReportType"
              required
              validateStatus={errors && errors["ReportType"] ? "error" : ""}
              help={errors.ReportType?.message}
            >
              <Controller
                control={control}
                name="ReportType"
                render={({ field }) => (
                  <Select
                    {...field}
                    virtual={false}
                    onChange={(v, o) => {
                      if (getValues().ReportType === reportTypes.DETAIL) {
                        setValue("SortGroupFields", []);
                      } else if (v === reportTypes.DETAIL) {
                        setValue("DrillDownSortFields", []);
                      }
                      field.onChange(v, o);
                      getExtraDisableDefaultOptions(getValues().AgencyKeys, v);
                    }}
                    name="ReportType"
                    options={productionReportTypeOptions}
                  />
                )}
              />
            </Form.Item>

            <AgencyMultiSelectEx
              layout={defaultLayout}
              formLabel="Agency"
              required={true}
              agencies={currentAgencies}
              isLoading={isEmpty(defaultValues.AgencyKeys)}
              setValue={setValue}
              control={control}
              name="AgencyKeys"
              onChange={(value) => {
                var oldLength = getValues().AgencyKeys.length;
                var newLength = value.length;
                // We only need to update default options in some cases
                if (
                  newLength != oldLength &&
                  ((oldLength < 2 && newLength > oldLength) || newLength < 2)
                ) {
                  getExtraDisableDefaultOptions(value, getValues().ReportType);
                }
              }}
            />
          </Col>

          <Col xs={24} sm={24} md={24} lg={14} xl={14} xxl={14}>
            <Row>
              <Col xs={5} sm={7} md={6} lg={8} xl={6} xxl={7}>
                <Row justify="end">
                  <Col>
                    <CustomRadioGroup
                      control={control}
                      name="DateType"
                      afterChange={() => trigger()}
                      className="vertical-radio-group"
                      options={[
                        {
                          value: reportDateTypes.TRANSACTION_DATE,
                          label: (
                            <div style={{ minWidth: "103px" }}>
                              Transaction Date
                            </div>
                          ),
                        },
                        {
                          value: reportDateTypes.EXPIRY_DATE,
                          label: (
                            <div style={{ minWidth: "103px" }}>Expiry Date</div>
                          ),
                        },
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col xs={19} sm={17} md={18} lg={16} xl={18} xxl={17}>
                <AdvancedRangePicker
                  formItem={{
                    required: true,
                    label: null,
                  }}
                  control={control}
                  setValue={setValue}
                  allowClear={true}
                  name="TransactionDate"
                  format={dateFormat}
                  dateRangeConfig={dateRangeConfigs.TRANSACTION}
                  toDateEndingIsYesterday={toDateEndingIsYesterday}
                />
              </Col>
            </Row>

            <Form.Item
              {...defaultLayout}
              label="Include"
              name="Include"
              required
              valuePropName="checked"
              style={{ marginTop: "8px" }}
            >
              <Space direction="vertical">
                <Controller
                  control={control}
                  name="IncludeNonFleetNonGarage"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => {
                        setValue("IncludeNonFleetNonGarage", e.target.checked, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        if (
                          !getValues().IncludeFleetGarage &&
                          !e.target.checked
                        ) {
                          setValue("IncludeFleetGarage", true, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      inputRef={field.ref}
                      name="IncludeNonFleetNonGarage"
                    >
                      Non-Fleet and Non-Garage Policies
                    </Checkbox>
                  )}
                />
                <Controller
                  control={control}
                  name="IncludeFleetGarage"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      name="IncludeFleetGarage"
                      onChange={(e) => {
                        setValue("IncludeFleetGarage", e.target.checked, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        if (
                          !getValues().IncludeNonFleetNonGarage &&
                          !e.target.checked
                        ) {
                          setValue("IncludeNonFleetNonGarage", true, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      inputRef={field.ref}
                    >
                      Fleet and Garage Policies
                    </Checkbox>
                  )}
                />
                <Controller
                  control={control}
                  name="IncludeCancellations"
                  render={({ field }) => (
                    <Checkbox
                      inputRef={field.ref}
                      checked={field.value}
                      name="IncludeCancellations"
                      onChange={(e) => {
                        setValue("IncludeCancellations", e.target.checked, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                    >
                      Cancellations
                    </Checkbox>
                  )}
                />
              </Space>
            </Form.Item>

            <Form.Item
              {...defaultLayout}
              label="Leased Vehicle"
              required
              validateStatus={errors.LeasedVehicle ? "error" : ""}
              help={errors.LeasedVehicle?.message}
              style={{ marginBottom: 0 }}
            >
              <Space>
                <Controller
                  control={control}
                  name="IncludeLeased"
                  render={({ field }) => (
                    <Checkbox
                      inputRef={field.ref}
                      checked={field.value}
                      onChange={(e) => {
                        setValue("IncludeLeased", e.target.checked, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        if (
                          !getValues().IncludeNotLeased &&
                          !e.target.checked
                        ) {
                          setValue("IncludeNotLeased", true, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                    >
                      Yes
                    </Checkbox>
                  )}
                />
                <Controller
                  control={control}
                  name="IncludeNotLeased"
                  render={({ field }) => (
                    <Checkbox
                      inputRef={field.ref}
                      checked={field.value}
                      onChange={(e) => {
                        setValue("IncludeNotLeased", e.target.checked, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        if (!getValues().IncludeLeased && !e.target.checked) {
                          setValue("IncludeLeased", true, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                    >
                      No
                    </Checkbox>
                  )}
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <ExtraReportFilterContainer>
          <Row>
            <Col xs={24} sm={24} md={24} lg={10} xl={11} xxl={12}>
              <Form.Item
                name="ProducerCode1Keys"
                label="Producer 1"
                {...specialLayout}
              >
                <ProducerSelectEx
                  name="ProducerCode1Keys"
                  setValue={setValue}
                  control={control}
                  producerNumber={1}
                  taskKey={taskKey}
                />
              </Form.Item>
              <Form.Item
                name="ProducerCode2Keys"
                label="Producer 2"
                {...specialLayout}
              >
                <ProducerSelectEx
                  name="ProducerCode2Keys"
                  setValue={setValue}
                  control={control}
                  producerNumber={2}
                  taskKey={taskKey}
                />
              </Form.Item>
              <Form.Item name="BatcNumber" label="Batch" {...specialLayout}>
                <BatchRangeSelectEx
                  disabled={getValues().ReportType === reportTypes.COMPARISON}
                  agencyKeys={getValues().AgencyKeys}
                  transactionDate={getValues().TransactionDate}
                  setValue={setValue}
                  control={control}
                  useICBCStmtLogic={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24} lg={14} xl={13} xxl={12}>
              <SearchMultiSelectEx
                formLabel="ICBC Product(s)"
                layout={{
                  labelCol: {
                    xs: { span: 5 },
                    sm: { span: 5 },
                    md: { span: 5 },
                    lg: { span: 9 },
                    xl: { span: 9 },
                    xxl: { span: 7 },
                  },
                  wrapperCol: {
                    xs: { span: 19 },
                    sm: { span: 19 },
                    md: { span: 19 },
                    lg: { span: 15 },
                    xl: { span: 15 },
                    xxl: { span: 17 },
                  },
                }}
                control={control}
                name="ProductKeys"
                data={policyProducts}
                setValue={setValue}
              />
              <Form.Item
                name="Customer"
                label="Customer"
                labelCol={{
                  xs: { span: 5 },
                  sm: { span: 5 },
                  md: { span: 5 },
                  lg: { span: 9 },
                  xl: { span: 9 },
                  xxl: { span: 7 },
                }}
                wrapperCol={{
                  xs: { span: 19 },
                  sm: { span: 19 },
                  md: { span: 19 },
                  lg: { span: 15 },
                  xl: { span: 15 },
                  xxl: { span: 17 },
                }}
              >
                <Controller
                  control={control}
                  name="Customer"
                  render={({ field }) => <Input {...field} name="Customer" />}
                />
              </Form.Item>
              <Form.Item
                tooltip={fleetGarageCommaListMessage}
                name="FleetGaragePolicyNumbers"
                label="Fleet # or Garage Policy #"
                labelCol={{
                  xs: { span: 7 },
                  sm: { span: 10 },
                  md: { span: 8 },
                  lg: { span: 9 },
                  xl: { span: 9 },
                  xxl: { span: 7 },
                }}
                wrapperCol={{
                  xs: { span: 17 },
                  sm: { span: 14 },
                  md: { span: 16 },
                  lg: { span: 15 },
                  xl: { span: 15 },
                  xxl: { span: 17 },
                }}
                validateStatus={
                  errors && errors["FleetGaragePolicyNumbers"] ? "error" : ""
                }
                help={errors.FleetGaragePolicyNumbers?.message}
              >
                <Controller
                  control={control}
                  name="FleetGaragePolicyNumbers"
                  render={({ field }) => (
                    <Input
                      {...field}
                      name="FleetGaragePolicyNumbers"
                      disabled={!getValues().IncludeFleetGarage}
                    />
                  )}
                />
              </Form.Item>
              <Form.Item
                tooltip={rateClassCommaListMessage}
                name="RateClass"
                label="Rate Class"
                labelCol={{
                  xs: { span: 7 },
                  sm: { span: 10 },
                  md: { span: 8 },
                  lg: { span: 9 },
                  xl: { span: 9 },
                  xxl: { span: 7 },
                }}
                wrapperCol={{
                  xs: { span: 17 },
                  sm: { span: 14 },
                  md: { span: 16 },
                  lg: { span: 15 },
                  xl: { span: 15 },
                  xxl: { span: 17 },
                }}
                validateStatus={errors && errors["RateClass"] ? "error" : ""}
                help={
                  errors.RateClass ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: errors.RateClass.message,
                      }}
                    />
                  ) : (
                    ""
                  )
                }
              >
                <Controller
                  control={control}
                  name="RateClass"
                  render={({ field }) => <Input {...field} name="RateClass" />}
                />
              </Form.Item>
            </Col>
          </Row>
        </ExtraReportFilterContainer>

        <Row>
          <Col span={24}>
            {getValues().ReportType === reportTypes.DETAIL ? (
              <SortGroupFieldSelectEx
                fieldOptions={sortGroupFieldOptions(includeFleetGarage)}
                control={control}
                setValue={setValue}
                getValues={getValues}
                trigger={trigger}
                isSubmitted={isSubmitted}
              />
            ) : (
              <DrillDownSortFieldSelectEx
                fieldOptions={
                  getValues().ReportType === reportTypes.SUMMARY
                    ? drillDownSortFieldOptions(includeFleetGarage)
                    : getValues().ReportType === reportTypes.SALES_SUMMARY ||
                      getValues().ReportType === reportTypes.SUMMARY_CONDENSED
                    ? salesAndCondensedDrillDownFieldOptions(includeFleetGarage)
                    : comparisonDrillDownFieldOptions(includeFleetGarage)
                }
                control={control}
                trigger={trigger}
                isSubmitted={isSubmitted}
              />
            )}
          </Col>
        </Row>
      </ReportPanel>
    </PermissionWrapper>
  );
};
export default ProductionReport;
